/**
 * https://docs.sentry.io/product/issues/issue-details/breadcrumbs/#breadcrumb-attributes
 * @typedef Breadcrumb
 * @property {String} category
 * @property {String=} message
 * @property {String} type
 * @property {object=} data
 * @property {String} level
 */
import type { Breadcrumb, BreadcrumbHint } from '@sentry/react';
/**
 * This function is designed to accept an html element, and return a human readable string that can be used to identify the html element on the page
 * @param {HTMLElement} element
 * @param {number} recursionDepth
 * @returns {string|null}
 */
export function getReadableElementName(element: HTMLElement, recursionDepth = 0): string | null {
  const MAX_RECURSION_DEPTH = 5;
  if (recursionDepth > MAX_RECURSION_DEPTH) return null;

  if (element.tagName === 'BODY') return '<body>';
  // return easily identified labels if available
  if (element.id) return `#${element.id}`; // the most unique identifier available
  if (element.ariaLabel) return element.ariaLabel;
  if (element.innerText) return element.innerText;
  // alt is specific to HTMLImageElement
  if ('alt' in element) {
    const imgElement = element as HTMLImageElement;
    if (imgElement.alt) return imgElement.alt;
  }

  // if the element has label elements attached, use the first label element
  // labels is specific to form elements like HTMLInputElement
  if ('labels' in element) {
    const formElement = element as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    if (formElement.labels?.length) {
      return getReadableElementName(formElement.labels[0], recursionDepth + 1);
    }
  }
  // check the parent element for the same values
  if (element.parentElement) {
    return getReadableElementName(element.parentElement, recursionDepth + 1);
  }

  return null;
}

export function handleUiClickBreadcrumb(
  breadcrumb: Breadcrumb,
  hint: BreadcrumbHint
): Breadcrumb | null {
  const { target: htmlElement } = hint.event as { target?: HTMLElement };
  if (!htmlElement) return breadcrumb;

  const elementName = getReadableElementName(htmlElement);
  if (elementName) breadcrumb.message = elementName;
  return breadcrumb;
}

export function handleFetchBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  const { url } = breadcrumb.data as { url?: string };
  if (!url) return breadcrumb;

  // filter out fetch calls that are not useful
  if (
    url.startsWith('https://atlas.microsoft.com/styling') ||
    url.startsWith('https://atlas.microsoft.com/map/') ||
    url.startsWith('data:image')
  ) {
    return null;
  }

  if (url.includes('emboldhealth.com/events/')) {
    // this allows us to quickly differentiate or filter between analytics fetches and data fetches when debugging in sentry
    breadcrumb.category = 'analytics';
  }

  return breadcrumb;
}

/**
 * This function runs before every breadcrumb is added to the Sentry scope.
 * Returning null will filter the breadcrumb out.
 * @param {Breadcrumb} breadcrumb
 * @param {*} hint
 * @returns {Breadcrumb|null}
 */
export default function beforeBreadcrumb(
  breadcrumb: Breadcrumb,
  hint: BreadcrumbHint
): Breadcrumb | null {
  switch (breadcrumb.category) {
    case 'ui.click':
    case 'ui.input':
      return handleUiClickBreadcrumb(breadcrumb, hint);
    case 'fetch':
    case 'xhr':
      return handleFetchBreadcrumb(breadcrumb);
    default:
      return breadcrumb;
  }
}
